import React, { useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { VALIDATION_STATES } from '../../utils/constants';
import ComposedSelectField from '../ComposedField/ComposedSelectField';
import FirstName from '../Fields/FirstName';
import LastName from '../Fields/LastName';
import MiddleName from '../Fields/MiddleName';
import { EDUCATION_LEVEL_OPTIONS } from '../../utils/registrationFormDropdownOptions';
import { breakpoints, GridContainer } from '@parchment/atoms';
import DateOfBirthGrid from '../DateOfBirthGrid/DateOfBirthGrid';
import { parseDate } from '../../utils/dates';
const INITIAL_VALIDATION_STATE = {
  firstName: {
    state: VALIDATION_STATES.neutral
  },
  middleName: {
    state: VALIDATION_STATES.neutral
  },
  lastName: {
    state: VALIDATION_STATES.neutral
  },
  dateOfBirth: {
    state: VALIDATION_STATES.neutral
  },
  highestLevelOfEducation: {
    state: VALIDATION_STATES.neutral
  }
};
const noop = () => undefined;
const VALIDATION_STATE_REDUCER = (prevState, updatedProperty) => ({
  ...prevState,
  ...updatedProperty
});
export default function LearnerInformationGrid(_ref) {
  let {
    fieldProps,
    onChange = noop,
    onValidate = noop
  } = _ref;
  const [validationStates, setValidationStates] = useReducer(VALIDATION_STATE_REDUCER, INITIAL_VALIDATION_STATE);
  const [data, updateData] = useReducer((state, action) => {
    let nextState = state;
    Object.entries(action).forEach(_ref2 => {
      let [key, value] = _ref2;
      if (value !== state[key]) {
        nextState = {
          ...nextState,
          [key]: value
        };
      }
    });
    return nextState;
  }, fieldProps);
  const {
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    highestLevelOfEducation
  } = data;
  useEffect(() => updateData(fieldProps), [fieldProps]);
  useEffect(() => onChange(data), [data]);
  useEffect(() => {
    const states = Object.values(validationStates).map(field => field.state);
    if (states.indexOf(VALIDATION_STATES.error) >= 0) {
      return onValidate(VALIDATION_STATES.error);
    } else if (states.indexOf(VALIDATION_STATES.neutral) >= 0) {
      return onValidate(VALIDATION_STATES.neutral);
    } else if (states.indexOf(VALIDATION_STATES.success) >= 0) {
      return onValidate(VALIDATION_STATES.success);
    }
  }, [validationStates]);
  return /*#__PURE__*/React.createElement(LearnerInformationFormGrid, {
    "data-testid": "learner-information-grid"
  }, /*#__PURE__*/React.createElement(GridContainer, {
    className: "learner-name-grid"
  }, /*#__PURE__*/React.createElement(FirstName, {
    className: "first-name-field",
    value: firstName,
    internalLabel: true,
    onChange: value => updateData({
      firstName: value || ''
    }),
    onValidate: validationState => setValidationStates({
      firstName: {
        state: validationState
      }
    }),
    required: true
  }), /*#__PURE__*/React.createElement(MiddleName, {
    className: "middle-name-field",
    value: middleName,
    internalLabel: true,
    onChange: value => updateData({
      middleName: value || ''
    }),
    onValidate: validationState => setValidationStates({
      middleName: {
        state: validationState
      }
    })
  }), /*#__PURE__*/React.createElement(LastName, {
    className: "last-name-field",
    value: lastName,
    internalLabel: true,
    onChange: value => updateData({
      lastName: value || ''
    }),
    onValidate: validationState => setValidationStates({
      lastName: {
        state: validationState
      }
    }),
    required: true
  }), /*#__PURE__*/React.createElement(DateOfBirthGrid, {
    value: dateOfBirth,
    onChange: value => updateData({
      dateOfBirth: value || ''
    }),
    onValidate: validationState => setValidationStates({
      dateOfBirth: {
        state: validationState
      }
    }),
    validators: [{
      message: 'You must be at least 13 years of age to use this service',
      callback: () => isUnderAge13(dateOfBirth)
    }]
  })), /*#__PURE__*/React.createElement(GridContainer, {
    className: "highest-education-level-field"
  }, /*#__PURE__*/React.createElement(ComposedSelectField, {
    id: "highest-education-level",
    name: "educationLevel",
    label: "Highest Level Of Education",
    value: highestLevelOfEducation,
    options: EDUCATION_LEVEL_OPTIONS,
    onChange: value => updateData({
      highestLevelOfEducation: value || ''
    }),
    onValidate: validationState => setValidationStates({
      highestLevelOfEducation: {
        state: validationState
      }
    }),
    minWidth: "100%",
    required: true
  })));
}
const isUnderAge13 = dateOfBirthStr => {
  const dateOfBirthObj = parseDate(dateOfBirthStr);
  if (dateOfBirthObj.isValid) {
    const timeDiff = dateOfBirthObj.diffNow('years');
    return timeDiff.years < -13;
  }
  return true;
};
const LearnerInformationFormGrid = /*#__PURE__*/styled.div.withConfig({
  displayName: "LearnerInformationGrid__LearnerInformationFormGrid",
  componentId: "sc-4ewo56-0"
})([".first-name-field,.middle-name-field,.last-name-field,.month-field,.day-field,.year-field{width:100%;flex-grow:1;@media (min-width:", "){width:33%;}}.learner-name-grid .first-name-field span > div{;border-bottom-left-radius:0;}.learner-name-grid .last-name-field span > div{border-bottom-right-radius:0;}#date-of-birth-grid{width:100%;margin-top:-1px;.month-field > div{border-top-left-radius:0;border-bottom-left-radius:5px;}.year-field > div{border-top-right-radius:0;border-bottom-right-radius:5px;}}.highest-education-level-field{padding-top:0;margin-top:0;button{border-top-left-radius:0;border-top-right-radius:0;}}"], breakpoints.mediumMin);